/* eslint-disable complexity */
import { createNewLiveReport } from '../../predict/src/core/api/reporting/report-builder';
import { fetchReportTemplateById } from '../../predict/src/core/api/reporting/report-templates';
import { getNumberOfPagesInTemplate, getPageHeightFromSetup } from '../../predict/src/report-builder/utils-layout';
import { LayoutData, PageSetup } from '../../predict/src/report-builder/types';
import { LayoutItem as Layout } from '../../predict/src/report-builder/components/grid-layout';

import { createMutation } from '../utils/mutations';
import { liveReportsQueryKey } from '../queries/liveReports';
import { queryClient } from '../core/queryClient';

export interface CreateReportInput {
  name: string;
  locale?: string;
  orientation?: 'portrait' | 'landscape' | 'dashboard';
  templateId?: string;
}
type CreateReportResult = Awaited<ReturnType<typeof createNewLiveReport>>;

interface SourceTemplateData {
  pageLayout?: Layout[];
  pageLayoutData?: LayoutData[];
  pageSetup?: Record<string, unknown>;
}

/**
 * Copies a source template's layout into the new report payload.
 *
 * The new creation sidebar does not open ReportBuilder, so it cannot rely on the editor to insert
 * and autosave the template after the report has been created.
 */
const getTemplateReportData = (sourceTemplateData: SourceTemplateData | undefined, templateId?: string) => {
  if (!templateId) {
    return {};
  }

  return {
    sourceTemplateId: templateId,
    pageLayout: sourceTemplateData?.pageLayout ?? [],
    pageLayoutData: sourceTemplateData?.pageLayoutData ?? []
  };
};

export const createReportMutation = (workspaceId: string, organisationId: string, isConsolidation: boolean) =>
  createMutation<CreateReportInput, CreateReportResult>({
    mutationFn: async (input: CreateReportInput) => {
      let sourceTemplate: Awaited<ReturnType<typeof fetchReportTemplateById>> | undefined;

      if (input.templateId) {
        sourceTemplate = await fetchReportTemplateById(
          workspaceId,
          organisationId,
          input.templateId,
          isConsolidation
        );
      }

      const sourceTemplateData = sourceTemplate?.data as SourceTemplateData | undefined;
      const pageSetup = input.templateId
        ? sourceTemplateData?.pageSetup
        : { orientation: input.orientation ?? 'portrait' };

      const orientation = sourceTemplate
        ? sourceTemplate.orientation
        : input.orientation ?? 'portrait';

      const pageCount = sourceTemplate
        ? getNumberOfPagesInTemplate(
            (sourceTemplate.data as { pageLayout: Layout[] })?.pageLayout || [],
            getPageHeightFromSetup(pageSetup as unknown as PageSetup)
          )
        : 1;

      return createNewLiveReport(
        workspaceId,
        organisationId,
        {
          name: input.name,
          locale: input.locale,
          orientation,
          page_count: pageCount,
          data: {
            pageSetup,
            userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            ...getTemplateReportData(sourceTemplateData, input.templateId),
            ...(sourceTemplate?.data_reference_mapping
              ? { data_reference_mapping: sourceTemplate.data_reference_mapping }
              : {})
          }
        },
        isConsolidation
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: liveReportsQueryKey(organisationId, workspaceId, isConsolidation)
      });
    }
  });
